import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Clock3, XCircle } from "lucide-react";
import { API } from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id") || "";
  const [state, setState] = useState("loading"); // loading | paid | pending | error
  const [attempt, setAttempt] = useState(0); // relance manuelle depuis l'état « Validation en cours »

  useEffect(() => {
    // Paiement désactivé (prod) → on ne laisse pas traîner cette page
    fetch(`${API}/public/config`).then((r) => r.json()).then((d) => {
      if (!d.payments_enabled) window.location.replace("/");
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    setState("loading");
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const r = await fetch(`${API}/payments/status/${sessionId}`);
        if (!r.ok) throw new Error("introuvable");
        const d = await r.json();
        if (d.payment_status === "paid") { setState("paid"); return; }
      } catch { /* on retente */ }
      tries += 1;
      if (tries >= 15) { setState("pending"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, attempt]);

  return (
    <div className="min-h-screen bg-[#0A0B10] text-white flex items-center justify-center p-6" data-testid="payment-success-page">
      <div className="w-full max-w-md border border-white/10 rounded-2xl p-10 text-center bg-white/[0.03]">
        {state === "loading" && (
          <>
            <Loader2 className="w-12 h-12 text-gold animate-spin mx-auto mb-6" />
            <h1 className="font-serif text-2xl mb-3">Vérification du paiement…</h1>
            <p className="text-sm text-[#A0B2CE]">Quelques secondes suffisent.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-6" data-testid="payment-confirmed-icon" />
            <h1 className="font-serif text-2xl mb-3" data-testid="payment-confirmed-title">Paiement confirmé</h1>
            <p className="text-sm text-[#A0B2CE] mb-8">
              Votre règlement a bien été reçu. Une confirmation vous a été envoyée par email
              et votre facture est marquée comme payée dans votre espace.
            </p>
          </>
        )}
        {state === "pending" && (
          <>
            <Clock3 className="w-14 h-14 text-gold mx-auto mb-6" data-testid="payment-pending-icon" />
            <h1 className="font-serif text-2xl mb-3">Validation en cours</h1>
            <p className="text-sm text-[#A0B2CE] mb-6">
              Votre paiement est en cours de validation par la banque. Vous recevrez un email
              de confirmation dès qu'il sera confirmé — inutile de payer une seconde fois.
            </p>
            <button onClick={() => setAttempt((a) => a + 1)} data-testid="payment-recheck-btn"
              className="mb-6 inline-flex items-center gap-2 border border-gold/50 text-gold px-5 py-2 rounded-full font-mono text-[11px] uppercase tracking-[0.12em] hover:bg-gold hover:text-[#0A0B10] transition-colors">
              Vérifier à nouveau
            </button>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="w-14 h-14 text-[#E74C3C] mx-auto mb-6" data-testid="payment-error-icon" />
            <h1 className="font-serif text-2xl mb-3">Session introuvable</h1>
            <p className="text-sm text-[#A0B2CE] mb-8">
              Impossible de vérifier ce paiement. Si vous avez été débité, contactez Le 30 du mois.
            </p>
          </>
        )}
        <Link to="/espace-client" data-testid="back-to-dashboard-link"
          className="inline-flex items-center gap-2 bg-gold text-[#0A0B10] px-6 py-2.5 rounded-full font-mono text-[11px] uppercase tracking-[0.12em] hover:bg-[#B5952F] transition-colors">
          Retour à mon espace
        </Link>
      </div>
    </div>
  );
}
